import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { inject, Injector, NgModule, provideAppInitializer } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { Router, RouterModule, Routes } from '@angular/router';
import { AbcCoreModule } from '@barthauer/abc/core';
import { AbcConfigService } from '@barthauer/abc/core/configuration';
import { BceModule } from '@barthauer/abc/management/configuration-explorer';
import {
  _AbcButtonComponent,
  _AbcDashboardComponent,
  AbcIconComponent,
  AbcTabPanelComponent,
  AbcUiComponentsModule,
} from '@barthauer/abc/ui-components';
import { BasysArenaComponent, BasysArenaModule } from '@barthauer/basys/arena';
import { BasysCoreService } from '@barthauer/basys/core';
import {
  AbcLoginComponentInfo,
  AbcProfileSelectionComponentInfo,
  AbcWebApiEditorComponentInfo,
  AuthenticationGuard,
  BasysCoreAuthenticationModule,
  BaSYSLoginComponent,
  BaSYSProfileSelectionComponent,
  BaSYSWebApiEditorComponent,
} from '@barthauer/basys/core/authentication';
import { BasysMapFrameComponent } from '@barthauer/basys/map-frame';
import { FlexLayoutModule } from 'ngx-flexible-layout';

// ← NEW: use the intermediate lib, not the old app
import { HasOrderGuard, OPERA_CONFIG } from '@barthauer/abc/maintenance/opera/core';
import { BaSYSOperaCoreModule } from '@barthauer/basys/opera/core';

import { OVERLAY_DEFAULT_CONFIG } from '@angular/cdk/overlay';
import { initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { getMessaging, isSupported, Messaging, provideMessaging } from '@angular/fire/messaging';
import { SafeResourceUrlPipe } from '@barthauer/abc/core';
import { DxTabPanelModule } from 'devextreme-angular';
import { AbcGridContextMenuService } from 'libs/abc/ui-components/src/lib/base/src/abcGrid/abcGridContextMenu.service';
import { FirebasePushAdapter } from '../../../../../libs/abc/core/notifications/firebase-push-adapter';
import { environment } from '../environments/environment';
import { AppComponent } from './app.component';
import { AppService } from './app.service';
import { ExternalLinkComponent } from './external-link/external-link.component';
import { HomeComponent } from './home/home.component';
import { MapComponent } from './map/map.component';
import { DataService } from './services/data.service';

const operaManagerRootRoutes: Routes = [
  {
    path: AbcLoginComponentInfo.ROUTE_PATH,
    component: BaSYSLoginComponent,
    data: {
      ApplicationTitle: 'BaSYS opera manager',
      ForwardTo: '/',
      LoginIntroHtml: 'BaS<span style="color:rgba(175, 202, 5, 1)">Y</span>S opera manager Login',
      MarkerTag: AbcLoginComponentInfo.ROUTE_MARKER_TAG,
    },
  },
  {
    path: AbcProfileSelectionComponentInfo.ROUTE_PATH,
    component: BaSYSProfileSelectionComponent,
    data: {
      ApplicationTitle: 'BaSYS opera manager',
      ForwardTo: '/',
      ForceReloadOnApply: true,
      MarkerTag: AbcProfileSelectionComponentInfo.ROUTE_MARKER_TAG,
    },
  },
  {
    path: AbcWebApiEditorComponentInfo.ROUTE_PATH,
    component: BaSYSWebApiEditorComponent,
    data: {
      ApplicationTitle: 'BaSYS opera manager',
      ForwardTo: '/',
      ForceReloadOnApply: true,
      MarkerTag: AbcWebApiEditorComponentInfo.ROUTE_MARKER_TAG,
    },
  },
  {
    path: 'Map',
    outlet: 'mainOutlet',
    component: BasysMapFrameComponent,
    canActivate: [AuthenticationGuard],
    data: { MarkerTag: 'Map' },
    children: [
      {
        path: '',
        outlet: 'mainOutlet',
        canActivate: [HasOrderGuard], // ← now from @barthauer/abc/maintenance/opera/core
        component: BasysMapFrameComponent,
      },
    ],
  },
  {
    path: '',
    component: BasysArenaComponent,
    canActivate: [AuthenticationGuard],
    data: {
      renderNavigation: true,
      applicationTitle: 'BaSYS opera manager',
      applicationIcon: 'BaSYS-opera-manager',
    },
  },
  {
    path: ':',
    component: BasysArenaComponent,
    canActivate: [AuthenticationGuard],
  },
];
@NgModule({
  declarations: [AppComponent, HomeComponent, ExternalLinkComponent, MapComponent],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    ReactiveFormsModule,
    FormsModule,
    SafeResourceUrlPipe,
    BasysArenaModule,
    BasysCoreAuthenticationModule,
    BaSYSOperaCoreModule,
    AbcUiComponentsModule,
    MatIconModule,
    FlexLayoutModule,
    AbcCoreModule,
    _AbcDashboardComponent,
    _AbcButtonComponent,
    AbcIconComponent,
    DxTabPanelModule,
    AbcTabPanelComponent,
    BceModule,
    RouterModule.forRoot(operaManagerRootRoutes),
  ],
  providers: [
    AuthenticationGuard,
    AbcGridContextMenuService,
    DataService,
    BasysCoreService,
    //CHE: Change this from imports to provider. Did not test it
    provideFirebaseApp(() =>
      initializeApp({
        apiKey: 'AIzaSyAianatnw-tpiuthSAEbPoOCesfeCFEcMg',
        //authDomain: 'YOUR_AUTH_DOMAIN',
        //databaseURL: 'YOUR_DATABASE_URL',
        projectId: 'barthauer-35ba9',
        storageBucket: 'barthauer-35ba9.appspot.com',
        messagingSenderId: 'opera',
        appId: '1:162505072953:android:f450de6ea958dec6d688e8',
      }),
    ),
    provideMessaging(() => getMessaging()),
    {
      provide: OPERA_CONFIG,
      useValue: {
        useDefaultRootRouting: false,
        appConfigUrl: environment.appConfigUrl,
      },
    },
    provideAppInitializer(() => {
      const initializerFn = ConfigurationFactory(inject(AbcConfigService), inject(AppService), inject(Router));
      return initializerFn();
    }),
    provideHttpClient(withInterceptorsFromDi()),
    { provide: OVERLAY_DEFAULT_CONFIG, useValue: { usePopover: false } },
  ],
})
export class AppModule {
  constructor(private injector: Injector) {
    this.initializeMessaging();
  }

  private async initializeMessaging(): Promise<void> {
    try {
      const supported = await isSupported();
      if (supported) {
        FirebasePushAdapter.messaging = this.injector.get(Messaging);
      } else {
        console.warn('Firebase Messaging is not supported on this browser.');
      }
    } catch (error) {
      console.error('Failed to initialize Firebase Messaging:', error);
    }
  }

  init() {
    const basysCoreService = this.injector.get(BasysCoreService);
    basysCoreService.init();
  }
}

export function ConfigurationFactory(globalConfig: AbcConfigService, appService: AppService, router: Router) {
  return () => {
    return new Promise((resolve) => {
      let path: string = ':';

      for (let i = 0; i < 10; i++) {
        path = path.concat('/:');
        router.config.push({
          path: path,
          component: BasysArenaComponent,
          canActivate: [AuthenticationGuard],
        });
      }
      appService.loadNavigationDocument();
      globalConfig.loadConfig(environment.appConfigUrl).then((result) => {
        resolve(result);
      });
      // .then(() => appService.configureRouter());
    });
  };
}
